/**
 * Auth Service for RecubeBE Panel
 * Handles Xbox/Bedrock authentication via login.paladium.games
 */

const AUTH_BASE_URL = 'https://login.paladium.games';
const TOKEN_KEY = 'recubebe_xbox_token';
/** Clé du jeton dans le stockage du navigateur (écoutée par les autres onglets, cf session.ts). */
export const TOKEN_STORAGE_KEY = TOKEN_KEY;
const AUTH_PENDING_KEY = 'recubebe_auth_pending';

export interface BedrockTokenPayload {
  xuid: string;
  gamertag: string;
  iat: number;
  exp: number;
}

export interface AuthResult {
  success: boolean;
  error?: string;
}

export interface MicrosoftValidateResponse {
  success: boolean;
  token?: string;
  profile?: {
    xuid: string;
    gamertag: string;
  };
  error?: boolean;
  message?: string;
}

/** Code du 503 de l'API quand la base des grades est illisible (jeton déjà vérifié). */
export const GRADE_BACKEND_UNAVAILABLE = 'GRADE_BACKEND_UNAVAILABLE';

export interface PanelAccessResult {
  hasAccess: boolean;
  gamertag?: string;
  reason?: string;
  /** api_error : code d'erreur renvoyé par l'API (ex. GRADE_BACKEND_UNAVAILABLE), s'il y en a un. */
  code?: string;
  /** Accès accordé par l'accès d'urgence (base des grades injoignable). */
  emergency?: boolean;
  /**
   * Seulement pour le 503 de la base des grades (code GRADE_BACKEND_UNAVAILABLE) : identifiant à
   * ajouter au secret PANEL_EMERGENCY_ADMINS pour obtenir l'accès d'urgence. Celui du corps du 503,
   * sinon le claim xuid du jeton, qui est exactement le champ que compare l'API.
   */
  emergencyId?: string;
}

class AuthService {
  /**
   * Get the OAuth login URL for Bedrock authentication
   * Redirects to login.paladium.games which handles Xbox OAuth
   */
  getLoginUrl(): string {
    return `${AUTH_BASE_URL}/microsoft/link?redirect_type=recubebe-panel&platform=bedrock`;
  }

  /**
   * Validate the OAuth code received from Microsoft callback
   * Exchanges the code for a JWT token via login.paladium.games
   */
  async validateCode(code: string): Promise<MicrosoftValidateResponse> {
    const response = await fetch(
      `${AUTH_BASE_URL}/microsoft/validate?code=${encodeURIComponent(code)}&redirect_type=recubebe-panel`
    );

    if (!response.ok) {
      return {
        success: false,
        error: true,
        message: 'Validation failed',
      };
    }

    return await response.json() as MicrosoftValidateResponse;
  }

  /**
   * Store the auth token
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Get the stored auth token
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Remove the auth token
   */
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  /**
   * Decode the JWT token (client-side only, no signature verification)
   */
  decodeToken(token: string): BedrockTokenPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) return null;
      // Base64url (alphabet - et _) : atob ne connaît que le base64 standard. Décodé en UTF-8 pour
      // qu'un handle accentué ne soit pas altéré.
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const bin = atob(b64);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      const payload = JSON.parse(new TextDecoder().decode(bytes));
      return payload as BedrockTokenPayload;
    } catch {
      return null;
    }
  }

  /**
   * Check if the token is valid and not expired
   */
  isTokenValid(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload) return false;

    const now = Math.floor(Date.now() / 1000);
    return payload.exp > now;
  }

  /**
   * Get user info from the stored token
   */
  getUserInfo(): BedrockTokenPayload | null {
    const token = this.getToken();
    if (!token) return null;

    if (!this.isTokenValid(token)) {
      this.clearToken();
      return null;
    }

    return this.decodeToken(token);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return this.isTokenValid(token);
  }

  /**
   * Set auth pending flag (for cross-window communication)
   */
  setAuthPending(pending: boolean): void {
    if (pending) {
      localStorage.setItem(AUTH_PENDING_KEY, 'true');
    } else {
      localStorage.removeItem(AUTH_PENDING_KEY);
    }
  }

  /**
   * Check if auth is pending
   */
  isAuthPending(): boolean {
    return localStorage.getItem(AUTH_PENDING_KEY) === 'true';
  }

  /**
   * Logout - clear all auth data
   */
  logout(): void {
    this.clearToken();
    this.setAuthPending(false);
  }

  /**
   * Check if user has panel access permission
   * Calls Quarkus backend /api/auth/check-access
   */
  async checkPanelAccess(): Promise<PanelAccessResult> {
    const token = this.getToken();
    if (!token) {
      return { hasAccess: false, reason: 'no_token' };
    }

    // Import dynamically to avoid circular dependency
    const { apiService } = await import('./api.service');
    const result = await apiService.checkPanelAccess();

    if (result.status === 403) {
      return { hasAccess: false, reason: 'no_permission' };
    }

    if (result.status === 401) {
      return { hasAccess: false, reason: 'invalid_token' };
    }

    if (result.error) {
      // Identifiant d'accès d'urgence : UNIQUEMENT pour le 503 de la base des grades, le seul cas où
      // l'accès d'urgence peut s'ouvrir. Réseau coupé, 500, 502 de proxy : pas d'identifiant, la
      // page n'invite pas à se faire ajouter à la liste pour une panne qu'elle ne réglerait pas.
      const gradeOutage = result.status === 503 && result.code === GRADE_BACKEND_UNAVAILABLE;
      const fromToken = gradeOutage ? this.decodeToken(token)?.xuid : undefined;
      return {
        hasAccess: false,
        reason: 'api_error',
        code: result.code,
        emergencyId: gradeOutage
          ? result.emergencyId ?? (typeof fromToken === 'string' && fromToken ? fromToken : undefined)
          : undefined,
      };
    }

    return {
      hasAccess: result.data?.hasAccess ?? false,
      gamertag: result.data?.gamertag ?? undefined,
      emergency: !!result.data?.emergency,
    };
  }
}

export const authService = new AuthService();
